import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropZone", "fileInput", "fileList", "progressBar", "folderSelect"]
  static values = {
    clientId: String,
    uploadUrl: String
  }

  connect() {
    this.setupDropZone()
  }

  setupDropZone() {
    if (!this.hasDropZoneTarget) return

    const dropZone = this.dropZoneTarget

    // Prevent default drag behaviors
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      dropZone.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })

    // Highlight drop zone when dragging over
    ;["dragenter", "dragover"].forEach(eventName => {
      dropZone.addEventListener(eventName, () => this.highlight(), false)
    })

    ;["dragleave", "drop"].forEach(eventName => {
      dropZone.addEventListener(eventName, () => this.unhighlight(), false)
    })

    // Handle dropped files
    dropZone.addEventListener("drop", (e) => this.handleDrop(e), false)
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight() {
    this.dropZoneTarget.classList.add("border-[#C4A484]", "bg-[#C4A484]/5")
    this.dropZoneTarget.classList.remove("border-[#E8E4DF]")
  }

  unhighlight() {
    this.dropZoneTarget.classList.remove("border-[#C4A484]", "bg-[#C4A484]/5")
    this.dropZoneTarget.classList.add("border-[#E8E4DF]")
  }

  handleDrop(e) {
    const files = e.dataTransfer.files
    this.handleFiles(files)
  }

  browseFiles() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = event.target.files
    this.handleFiles(files)
  }

  handleFiles(files) {
    const fileArray = [...files]

    // Check if folder is selected
    if (this.hasFolderSelectTarget && !this.folderSelectTarget.value) {
      alert("Please select a folder before uploading files.")
      return
    }

    fileArray.forEach(file => this.uploadFile(file))
  }

  async uploadFile(file) {
    const listItem = this.createFileListItem(file)

    const formData = new FormData()
    formData.append("upload[file]", file)
    if (this.hasFolderSelectTarget) {
      formData.append("upload[folder_type]", this.folderSelectTarget.value)
    }

    try {
      const response = await fetch(this.uploadUrlValue || `/sm/clients/${this.clientIdValue}/uploads`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        this.updateListItemStatus(listItem, "success")
        // Reload after short delay to show all uploads
        setTimeout(() => window.location.reload(), 1000)
      } else {
        this.updateListItemStatus(listItem, "error")
      }
    } catch (error) {
      console.error("Upload failed:", error)
      this.updateListItemStatus(listItem, "error")
    }
  }

  createFileListItem(file) {
    if (!this.hasFileListTarget) return null

    const item = document.createElement("div")
    item.className = "flex items-center justify-between p-3 bg-[#F5F3F0] rounded-lg"
    item.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="w-8 h-8 rounded bg-[#E8E4DF] flex items-center justify-center">
          ${this.getFileIcon(file.type)}
        </div>
        <div>
          <p class="text-sm font-medium text-[#2D2D2D]">${file.name}</p>
          <p class="text-xs text-[#8C8C8C]">${this.formatFileSize(file.size)}</p>
        </div>
      </div>
      <div class="upload-status">
        <svg class="w-5 h-5 text-[#8C8C8C] animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `

    this.fileListTarget.appendChild(item)
    return item
  }

  updateListItemStatus(item, status) {
    if (!item) return

    const statusDiv = item.querySelector(".upload-status")
    if (!statusDiv) return

    if (status === "success") {
      statusDiv.innerHTML = `
        <svg class="w-5 h-5 text-[#9CAF88]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
      `
    } else {
      statusDiv.innerHTML = `
        <svg class="w-5 h-5 text-[#E57373]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      `
    }
  }

  getFileIcon(mimeType) {
    if (mimeType.startsWith("image/")) {
      return `<svg class="w-4 h-4 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`
    } else if (mimeType.startsWith("video/")) {
      return `<svg class="w-4 h-4 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>`
    } else if (mimeType === "application/pdf") {
      return `<svg class="w-4 h-4 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>`
    }
    return `<svg class="w-4 h-4 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  deleteFile(event) {
    const fileId = event.currentTarget.dataset.fileId

    if (!confirm("Are you sure you want to delete this file?")) {
      return
    }

    fetch(`/sm/clients/${this.clientIdValue}/uploads/${fileId}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => {
      if (response.ok) {
        event.currentTarget.closest("[data-file-item]").remove()
      }
    })
  }
}
